import { useEffect, useRef } from "react";
import { ThermoImage } from "../types";
import { trackAnalyticsEvent } from "../analytics";
import { toAppFileUrl } from "../utils/fileUrls";
import { buildShowAssetUrl, isShowMode } from "../showMode";

type Props = {
  images: ThermoImage[];
  anomalies?: number[];
  nasMountPath?: string;
  onUnselect: (id: number) => void;
  onViewDetail: (image: ThermoImage) => void;
  onDelete?: (image: ThermoImage) => void;
  anomalyLoading?: boolean;
  anomalyError?: string | null;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  anomalyPage: number;
  onAnomalyPageChange: (page: number) => void;
};

function buildTempImagePath(nasMountPath: string | undefined, image: ThermoImage) {
  if (isShowMode) {
    return buildShowAssetUrl(`thermoimaging/output/${image.id}/temp_image.png`);
  }
  if (!nasMountPath) return "";
  const cleanedMount = nasMountPath.replace(/\\/g, "/").replace(/\/$/, "");
  const path = `${cleanedMount}/thermoimaging/output/${image.id}/temp_image.png`;
  return toAppFileUrl(path);
}

export default function Gallery({
  images,
  anomalies,
  nasMountPath,
  onUnselect,
  onViewDetail,
  onDelete,
  anomalyLoading,
  anomalyError,
  page,
  pageSize,
  onPageChange,
  anomalyPage,
  onAnomalyPageChange
}: Props) {
  const trackedOpenRef = useRef(false);
  const anomalySet = new Set(anomalies || []);
  const anomalyImages = images.filter((image) => anomalySet.has(image.id));
  const normalImages = images.filter((image) => !anomalySet.has(image.id));

  const totalPages = Math.max(1, Math.ceil(normalImages.length / pageSize));
  const start = page * pageSize;
  const paginated = normalImages.slice(start, start + pageSize);

  const anomalyTotalPages = Math.max(1, Math.ceil(anomalyImages.length / pageSize));
  const anomalyStart = anomalyPage * pageSize;
  const anomalyPaginated = anomalyImages.slice(anomalyStart, anomalyStart + pageSize);

  useEffect(() => {
    if (!isShowMode || trackedOpenRef.current || images.length === 0) return;
    trackedOpenRef.current = true;
    trackAnalyticsEvent("gallery_opened");
  }, [images.length]);

  const renderTile = (image: ThermoImage, isAnomaly: boolean, index: number) => {
    const imgPath = buildTempImagePath(nasMountPath, image);
    return (
      <div
        key={image.id}
        className="card"
        data-tour-id={!isAnomaly && index === 0 ? "thermo-gallery-first-card" : undefined}
        style={{
          background: "var(--surface-elevated)",
          borderColor: isAnomaly ? "#ff9f9f" : undefined
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 12,
            color: "var(--text-subtle)"
          }}
        >
          <span>{image.filename}</span>
          {isAnomaly && (
            <span
              className="pill"
              style={{
                background: "rgba(255, 159, 159, 0.12)",
                borderColor: "#ff9f9f",
                color: "#ffb3b3"
              }}
            >
              Anomaly
            </span>
          )}
        </div>
        <div
          style={{
            display: "grid",
            placeItems: "center",
            marginTop: 6
          }}
        >
          {imgPath ? (
            <img
              src={imgPath}
              alt={image.filename}
              style={{
                width: "min(120px, 100%)",
                aspectRatio: "1 / 1",
                height: "auto",
                objectFit: "cover",
                borderRadius: 8
              }}
            />
          ) : (
            <div
              style={{
                width: "min(120px, 100%)",
                aspectRatio: "1 / 1",
                height: "auto",
                display: "grid",
                placeItems: "center",
                background: "#0c1016",
                border: "1px dashed #1e2b3d",
                borderRadius: 8
              }}
                  >
                    {isShowMode ? "No preview" : "NAS not mounted"}
                  </div>
                )}
              </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            marginTop: 10,
            flexWrap: "wrap",
            justifyContent: "center"
          }}
        >
          <button
            className="icon-button"
            title="View Detail"
            onClick={() => {
              trackAnalyticsEvent("plot_view_opened");
              onViewDetail(image);
            }}
            aria-label="View Detail"
          >
            🔍
          </button>
          {!isShowMode && (
            <button
              className="icon-button"
              title="Unselect"
              onClick={() => onUnselect(image.id)}
              aria-label="Unselect"
            >
              ✖
            </button>
          )}
          {onDelete && (
            <button
              className="icon-button"
              title="Delete"
              onClick={() => onDelete(image)}
              aria-label="Delete"
            >
              🗑
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="card" data-tour-id="thermo-gallery">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="section-title">Gallery</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {anomalyLoading && <span className="pill">Detecting anomalies…</span>}
          {!anomalyLoading && anomalySet.size > 0 && (
            <span
              className="pill"
              style={{
                background: "rgba(255, 159, 159, 0.12)",
                borderColor: "#ff9f9f",
                color: "#ffb3b3"
              }}
            >
              Anomalies: {anomalySet.size}
            </span>
          )}
          <span className="pill">
            {isShowMode ? "Assets: show export" : `NAS: ${nasMountPath || "not mounted"}`}
          </span>
        </div>
      </div>
      {anomalyError && (
        <div className="status-error" style={{ marginTop: 10 }}>
          Anomaly detection failed: {anomalyError}
        </div>
      )}

      {!images.length && <div style={{ color: "var(--text-faint)", marginTop: 10 }}>No selections to display</div>}

      {images.length > 0 && (
        <>
          {anomalyImages.length > 0 && (
            <>
              <div className="label" style={{ marginTop: 12 }}>
                Anomalies ({anomalyImages.length})
              </div>
              <div
                className="responsive-gallery-grid"
                style={{ gridAutoRows: "1fr", marginTop: 10 }}
              >
                {anomalyPaginated.map((image, index) => renderTile(image, true, index))}
              </div>
              {anomalyImages.length > 0 && (
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                  <button
                    className="button secondary"
                    disabled={anomalyPage === 0}
                    onClick={() => onAnomalyPageChange(Math.max(0, anomalyPage - 1))}
                  >
                    Prev
                  </button>
                  <div className="pill">
                    Page {anomalyPage + 1} / {anomalyTotalPages}
                  </div>
                  <button
                    className="button secondary"
                    disabled={anomalyPage + 1 >= anomalyTotalPages}
                    onClick={() =>
                      onAnomalyPageChange(Math.min(anomalyTotalPages - 1, anomalyPage + 1))
                    }
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}

          <div className="label" style={{ marginTop: 14 }}>
            {anomalyImages.length ? "Other selections" : "Selections"}
          </div>
          <div
            className="responsive-gallery-grid"
            style={{ gridAutoRows: "1fr", marginTop: 10 }}
          >
            {paginated.map((image, index) => renderTile(image, false, index))}
            {paginated.length === 0 && (
              <div style={{ color: "var(--text-faint)" }}>
                {anomalyImages.length
                  ? "All selected samples are flagged as anomalies."
                  : "No selections to display"}
              </div>
            )}
          </div>
          {normalImages.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
              <button
                className="button secondary"
                disabled={page === 0}
                onClick={() => onPageChange(Math.max(0, page - 1))}
              >
                Prev
              </button>
              <div className="pill">
                Page {page + 1} / {totalPages}
              </div>
              <button
                className="button secondary"
                disabled={page + 1 >= totalPages}
                onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
